import React, { type FC } from 'react';

import Box from '../../../Box/Box';
import { Icon } from '../../../Icon';
import { Heading, Text } from '../../../Typography';

export type TopMatchingReviewProps = {
  /**
   * Name of the review author
   */
  author: string;
  /**
   * Date the review was posted (e.g., "Aug 25, 2025")
   */
  datePosted: string;
  /**
   * Rating from 1-5 (can be string or number)
   */
  rating: number | string;
  /**
   * Review content text
   */
  content?: string;
  /**
   * Title for the top matching review section
   * @default "Top matching review"
   */
  title?: string;
  /**
   * Additional CSS class names
   */
  className?: string;
  /**
   * Children content (used when rendering from markdown)
   */
  children?: React.ReactNode;
};

export const TopMatchingReview: FC<TopMatchingReviewProps> = ({
  author,
  datePosted,
  rating,
  content,
  title = 'Top matching review',
  className,
  children,
}) => {
  // Parse rating if it's a string (from markdown)
  const parsedRating =
    typeof rating === 'string' ? parseInt(rating, 10) : rating;

  // Use children content if content prop is not provided (for markdown rendering)
  const displayContent = content || children;

  return (
    <Box display="flex" flexDirection="column" gap="var(--spacing-2)">
      <Heading
        size="2xs"
        fontWeight="bold"
        color="var(--text-primary)"
        css={{ marginTop: 'var(--spacing-6)' }}
      >
        {title}
      </Heading>
      <Box
        display="flex"
        flexDirection="column"
        gap="var(--spacing-4)"
        bg="var(--surface-neutral)"
        borderRadius="var(--radius-lg)"
        pt="var(--spacing-2)"
        minWidth="335px"
        className={className}
      >
        <Text size="md" color="var(--text-primary)">
          {displayContent}
        </Text>
        <Box display="flex" alignItems="center" gap="var(--spacing-2)">
          <Icon variant="StarSolid" size="large" />
          <Text size="xs" color="var(--text-primary)">
            This review was posted by {author} on {datePosted} • {parsedRating}
            /5 Rating
          </Text>
        </Box>
      </Box>
    </Box>
  );
};
